import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Bell,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Filter,
  Eye,
  EyeOff,
  Check,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import Layout from "../components/layout/Layout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AlertsPage = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [processingAlert, setProcessingAlert] = useState(null);

  const fetchAlerts = useCallback(async () => {
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const response = await axios.get(`${API}/alerts${params}`);
      setAlerts(response.data);
    } catch (err) {
      toast.error("Failed to load alerts");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, API]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const handleIgnoreAlert = async (alertId) => {
    setProcessingAlert(alertId);
    try {
      await axios.post(`${API}/alerts/${alertId}/ignore`);
      toast.success("Alert ignored");
      fetchAlerts();
    } catch (error) {
      toast.error("Failed to ignore alert");
    } finally {
      setProcessingAlert(null);
    }
  };

  const handleResolveAlert = async (alertId) => {
    setProcessingAlert(alertId);
    try {
      await axios.post(`${API}/alerts/${alertId}/resolve`);
      toast.success("Alert marked as resolved");
      fetchAlerts();
    } catch (error) {
      toast.error("Failed to resolve alert");
    } finally {
      setProcessingAlert(null);
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case "critical":
        return <XCircle className="w-4 h-4 text-red-500" />;
      case "warning":
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      default:
        return <Bell className="w-4 h-4 text-blue-500" />;
    }
  };

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case "critical":
        return (
          <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/20">
            Critical
          </Badge>
        );
      case "warning":
        return (
          <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/20">
            Warning
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="bg-blue-500/10 text-blue-500 border-blue-500/20">
            Info
          </Badge>
        );
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return (
          <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/20">
            Active
          </Badge>
        );
      case "ignored":
        return (
          <Badge variant="outline" className="bg-zinc-500/10 text-zinc-400 border-zinc-500/20">
            Ignored
          </Badge>
        );
      case "resolved":
        return (
          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
            Resolved
          </Badge>
        );
      default:
        return null;
    }
  };

  const getAlertTypeLabel = (type) => {
    switch (type) {
      case "site_offline":
        return "Site Offline";
      case "failed_update":
        return "Failed Update";
      case "backup_overdue":
        return "Backup Overdue";
      case "vulnerability_warning":
        return "Vulnerability Warning";
      default:
        return type;
    }
  };

  // Stats
  const activeCount = alerts.filter((a) => a.status === "active").length;
  const resolvedCount = alerts.filter((a) => a.status === "resolved").length;
  const ignoredCount = alerts.filter((a) => a.status === "ignored").length;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-zinc-400">Loading alerts...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="alerts-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-50 font-['Outfit'] tracking-tight">
              Alerts
            </h1>
            <p className="text-zinc-500 mt-1">
              Monitor and manage site alerts
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="sheriff-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-amber-500/10 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-500 font-mono">{activeCount}</p>
                  <p className="text-xs text-zinc-500">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="sheriff-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-emerald-500/10 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-500 font-mono">{resolvedCount}</p>
                  <p className="text-xs text-zinc-500">Resolved</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="sheriff-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-zinc-500/10 flex items-center justify-center">
                  <EyeOff className="w-5 h-5 text-zinc-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-zinc-400 font-mono">{ignoredCount}</p>
                  <p className="text-xs text-zinc-500">Ignored</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-zinc-500" />
            <span className="text-sm text-zinc-500">Filter:</span>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger
              data-testid="status-filter"
              className="w-40 bg-zinc-900 border-zinc-800 text-slate-50"
            >
              <SelectValue placeholder="All Alerts" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all">All Alerts</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="ignored">Ignored</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Alerts Table */}
        <Card className="sheriff-card">
          <CardContent className="p-0">
            {alerts.length === 0 ? (
              <div className="p-12 text-center">
                <CheckCircle className="w-16 h-16 text-emerald-500/30 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-50 mb-2">
                  No alerts found
                </h3>
                <p className="text-zinc-500">
                  {statusFilter === "all"
                    ? "Your sites are running smoothly with no alerts"
                    : `No ${statusFilter} alerts at the moment`}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-zinc-500 font-semibold">Severity</TableHead>
                    <TableHead className="text-zinc-500 font-semibold">Site</TableHead>
                    <TableHead className="text-zinc-500 font-semibold">Type</TableHead>
                    <TableHead className="text-zinc-500 font-semibold">Message</TableHead>
                    <TableHead className="text-zinc-500 font-semibold">Status</TableHead>
                    <TableHead className="text-zinc-500 font-semibold">Created</TableHead>
                    <TableHead className="text-zinc-500 font-semibold text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {alerts.map((alert) => (
                    <TableRow
                      key={alert.alert_id}
                      data-testid={`alert-row-${alert.alert_id}`}
                      className="border-zinc-800 hover:bg-zinc-800/50"
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getSeverityIcon(alert.severity)}
                          {getSeverityBadge(alert.severity)}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium text-slate-50">
                        {alert.site_name || "Unknown"}
                      </TableCell>
                      <TableCell className="text-zinc-400">
                        {getAlertTypeLabel(alert.alert_type)}
                      </TableCell>
                      <TableCell className="text-zinc-400 max-w-[300px] truncate">
                        {alert.message}
                      </TableCell>
                      <TableCell>{getStatusBadge(alert.status)}</TableCell>
                      <TableCell className="text-zinc-500 text-sm">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(alert.created_at).toLocaleDateString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        {alert.status === "active" && (
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              data-testid={`ignore-alert-${alert.alert_id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => handleIgnoreAlert(alert.alert_id)}
                              disabled={processingAlert === alert.alert_id}
                              className="text-zinc-400 hover:text-white hover:bg-zinc-800"
                            >
                              <EyeOff className="w-4 h-4 mr-1" />
                              Ignore
                            </Button>
                            <Button
                              data-testid={`resolve-alert-${alert.alert_id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResolveAlert(alert.alert_id)}
                              disabled={processingAlert === alert.alert_id}
                              className="text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Resolve
                            </Button>
                          </div>
                        )}
                        {alert.status === "resolved" && alert.resolved_at && (
                          <span className="text-xs text-zinc-600">
                            Resolved: {new Date(alert.resolved_at).toLocaleDateString()}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AlertsPage;
